import { create } from 'zustand';
import type { MediaFingerprint, RoomSource } from '@cinesync/protocol';
import type { MediaDescriptor } from './engine';
import type { SubtitleTrack } from './subtitles';

/**
 * What is currently open in the player.
 *
 * Kept in a store rather than in route state so that a local file, which
 * cannot survive a page reload as a URL, is not lost by navigating within the
 * app. The object URL lives exactly as long as this entry does.
 */

export interface OpenMedia {
  /** Stable id, used as the storage key for resume and bookmarks. */
  readonly id: string;
  readonly descriptor: MediaDescriptor;
  readonly fingerprint: string | null;
  readonly sizeBytes: number | null;
  /** Set when the media came from a local file, so the URL can be revoked. */
  readonly objectUrl: string | null;
}

export interface LoadedSubtitles {
  readonly label: string;
  readonly track: SubtitleTrack;
  /** Manual delay adjustment in milliseconds. */
  readonly offsetMs: number;
}

interface PlaybackStore {
  readonly current: OpenMedia | null;
  readonly subtitles: LoadedSubtitles | null;
  readonly subtitlesVisible: boolean;

  open: (media: OpenMedia) => void;
  close: () => void;
  setSubtitles: (subtitles: LoadedSubtitles | null) => void;
  setSubtitleOffset: (offsetMs: number) => void;
  toggleSubtitles: () => void;
}

export const usePlaybackStore = create<PlaybackStore>((set, get) => ({
  current: null,
  subtitles: null,
  subtitlesVisible: true,

  open: (media) => {
    // Release the previous object URL before losing the reference to it,
    // otherwise the whole file stays resident for the life of the session.
    const previous = get().current;
    if (previous?.objectUrl !== null && previous?.objectUrl !== undefined) {
      URL.revokeObjectURL(previous.objectUrl);
    }
    set({ current: media, subtitles: null });
  },

  close: () => {
    const previous = get().current;
    if (previous?.objectUrl !== null && previous?.objectUrl !== undefined) {
      URL.revokeObjectURL(previous.objectUrl);
    }
    set({ current: null, subtitles: null });
  },

  setSubtitles: (subtitles) => set({ subtitles, subtitlesVisible: subtitles !== null }),

  setSubtitleOffset: (offsetMs) =>
    set((state) =>
      state.subtitles === null ? state : { subtitles: { ...state.subtitles, offsetMs } },
    ),

  toggleSubtitles: () => set((state) => ({ subtitlesVisible: !state.subtitlesVisible })),
}));

/**
 * Build an `OpenMedia` from a file the user dropped or picked.
 *
 * The id is derived from the fingerprint so that reopening the same film,
 * from a different folder or under a different name, finds its existing resume
 * position and bookmarks rather than starting again.
 */
export function openMediaFromFile(file: File, fingerprint: string): OpenMedia {
  const objectUrl = URL.createObjectURL(file);
  return {
    id: `file:${fingerprint}`,
    descriptor: {
      url: objectUrl,
      title: stripExtension(file.name),
      mimeType: file.type.length > 0 ? file.type : null,
    },
    fingerprint,
    sizeBytes: file.size,
    objectUrl,
  };
}

function stripExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * Build an `OpenMedia` from a source the room announced.
 *
 * The counterpart to `openMediaFromFile`, for the case where the film is not
 * on this machine at all. There is no fingerprint because there is no file to
 * sample, and none is needed: two people playing the same URL are by
 * definition playing the same bytes.
 */
export function openMediaFromUrl(source: RoomSource): OpenMedia {
  return {
    id: `url:${source.url}`,
    descriptor: {
      url: source.url,
      title: source.title,
      mimeType: source.mimeType,
    },
    fingerprint: null,
    sizeBytes: null,
    objectUrl: null,
  };
}

/**
 * The wire fingerprint for what is open, if it can be established.
 *
 * Returns `null` for anything opened from a URL rather than a file. That is
 * deliberate: claiming a fingerprint that was never computed would make the
 * mismatch check answer confidently and wrongly.
 */
export function toMediaFingerprint(
  media: OpenMedia | null,
  durationMs: number | null,
): MediaFingerprint | null {
  if (media === null || media.fingerprint === null || media.sizeBytes === null) return null;
  return {
    sampleHash: media.fingerprint,
    sizeBytes: media.sizeBytes,
    durationMs: durationMs ?? 0,
    displayName: media.descriptor.title.slice(0, 255),
  };
}
